/**
 * CP self-service: attributed wallet users list + CSV/XLSX export.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, Search, Users } from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';

interface AttributedUserRow {
  userRef: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  kycStatus: string;
  walletStatus: string;
  registeredAt: string;
}

interface UserStats {
  totalUsers: number;
  kycVerified: number;
  kycPending: number;
  kycRejected: number;
  registrations30d: number;
}


const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-ZA');
};

const KYC_COLORS: Record<string, string> = {
  verified: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
  pending: 'bg-amber-50 text-amber-700',
};

export const ClientAttributedUsersOverlay: React.FC = () => {
  const { apiBase, authHeaders } = useCpPortal();
  const [users, setUsers] = useState<AttributedUserRow[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [exportingFormat, setExportingFormat] = useState<'csv' | 'xlsx' | null>(null);
  const LIMIT = 20;

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, kycFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/attributed-users/summary`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (json.success) setStats(json.data.userStats);
    } catch {
      /* non-fatal */
    }
  }, [apiBase, authHeaders]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(LIMIT));
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (kycFilter) params.set('kycStatus', kycFilter);

      const res = await fetch(`${apiBase}/attributed-users?${params.toString()}`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load users');
      setUsers(json.data.users || []);
      setTotal(json.data.pagination?.total || 0);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, kycFilter, apiBase, authHeaders]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExportingFormat(format);
    try {
      const params = new URLSearchParams();
      params.set('format', format);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (kycFilter) params.set('kycStatus', kycFilter);

      const res = await fetch(`${apiBase}/attributed-users/export?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Export failed');
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const fallback = format === 'xlsx' ? 'channel-partner-users.xlsx' : 'channel-partner-users.csv';
      const filename = match?.[1] || fallback;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExportingFormat(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT) || 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Registered users</h1>
          <p className="text-sm text-gray-500 mt-1">
            Reps who registered through your partner link.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { fetchUsers(); fetchSummary(); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => handleExport('csv')}
            disabled={exportingFormat !== null}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exportingFormat === 'csv' ? 'Exporting...' : 'Download CSV'}
          </button>
          <button
            type="button"
            onClick={() => handleExport('xlsx')}
            disabled={exportingFormat !== null}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)] disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exportingFormat === 'xlsx' ? 'Exporting...' : 'Download Excel'}
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total users', value: stats.totalUsers },
            { label: 'KYC verified', value: stats.kycVerified },
            { label: 'KYC pending', value: stats.kycPending },
            { label: 'KYC rejected', value: stats.kycRejected },
            { label: 'New (30 days)', value: stats.registrations30d },
          ].map((kpi) => (
            <div key={kpi.label} className="mymoolah-card p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-medium uppercase mb-1">
                <Users className="h-3.5 w-3.5" />
                {kpi.label}
              </div>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mymoolah-card overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-green-300"
            />
          </div>
          <select
            value={kycFilter}
            onChange={(e) => setKycFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
          >
            <option value="">All KYC</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {error && (
          <div className="mx-4 mt-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                {['User ref', 'Name', 'Phone', 'Email', 'KYC', 'Wallet', 'Registered'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">Loading...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    No registered users yet. Share your partner registration link to onboard reps.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.userRef} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{u.userRef}</td>
                    <td className="px-4 py-3 text-gray-800">
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.phoneNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{u.email || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${KYC_COLORS[u.kycStatus] || KYC_COLORS.pending}`}>
                        {u.kycStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{u.walletStatus}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(u.registeredAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
            <span>Page {page} of {totalPages} ({total} users)</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
